import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReviewTabs from './ReviewTabs';

// Editable AI prompts for the code reviewer. The system prompt, the two
// section headers and the user-prompt template live in app_settings (with
// built-in defaults); standard rule packs carry their own injected prompt_text.

const PROMPT_FIELDS = [
  {
    key: 'review_system_prompt',
    label: 'System prompt',
    rows: 14,
    hint: 'The reviewer’s core instructions. Standard rule packs and custom rules are appended automatically after this.',
  },
  {
    key: 'review_user_template',
    label: 'User prompt template',
    rows: 12,
    hint: 'Built per PR. Must keep {diff_block}; other placeholders are optional.',
    placeholders: true,
  },
  {
    key: 'review_packs_header',
    label: 'Standard packs intro line',
    rows: 2,
    hint: 'Printed before the enabled rule packs in the system prompt.',
  },
  {
    key: 'review_rules_header',
    label: 'Custom rules intro line',
    rows: 2,
    hint: 'Printed before your custom rules in the system prompt.',
  },
  {
    key: 'review_repo_rules_header',
    label: 'Repo rules intro line (code-review.md)',
    rows: 3,
    hint: 'Printed before a repository’s own rules, pulled from a code-review.md file in its root (any casing / separator). Applied automatically per repo when that file exists.',
  },
];

export default function ReviewPrompts() {
  const [data, setData] = useState({});
  const [defaults, setDefaults] = useState({});
  const [placeholders, setPlaceholders] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingPrompts, setSavingPrompts] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pk] = await Promise.all([
        axios.get('/code-review/prompts'),
        axios.get('/code-review/packs'),
      ]);
      setData(p.data.data || {});
      setDefaults(p.data.defaults || {});
      setPlaceholders(p.data.placeholders || []);
      setPacks(pk.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  function setField(k, v) { setData(prev => ({ ...prev, [k]: v })); setNotice(''); }
  function resetField(k) { setField(k, defaults[k] || ''); }

  async function savePrompts() {
    setError(''); setNotice('');
    const tmpl = data.review_user_template || '';
    if (tmpl.trim() && !tmpl.includes('{diff_block}')) {
      setError('The user prompt template must contain the {diff_block} placeholder.');
      return;
    }
    setSavingPrompts(true);
    try {
      await axios.put('/code-review/prompts', {
        review_system_prompt: data.review_system_prompt || '',
        review_user_template: data.review_user_template || '',
        review_packs_header: data.review_packs_header || '',
        review_rules_header: data.review_rules_header || '',
        review_repo_rules_header: data.review_repo_rules_header || '',
      });
      setNotice('Prompts saved. New scans will use the updated prompts.');
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save prompts');
    } finally {
      setSavingPrompts(false);
    }
  }

  async function savePack(pack) {
    if (!(pack.prompt_text || '').trim()) { alert('Pack prompt cannot be empty'); return; }
    try {
      await axios.put(`/code-review/packs/${pack.pack_key}`, {
        name: pack.name, description: pack.description,
        prompt_text: pack.prompt_text, enabled: pack.enabled,
      });
      setNotice(`Rule pack “${pack.name}” saved.`);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save pack');
    }
  }
  function setPack(key, field, value) {
    setPacks(prev => prev.map(p => p.pack_key === key ? { ...p, [field]: value } : p));
    setNotice('');
  }

  if (loading) return <div className="page-body"><ReviewTabs /><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h2>Prompts</h2>
        <p>Edit the AI reviewer’s instructions. Changes apply to the next scan; leave a field blank to fall back to the built-in default.</p>
      </div>
      <div className="page-body">
        <ReviewTabs />
        {error && <div className="error-msg">{error}</div>}
        {notice && <div className="alert alert-success" style={{ marginBottom: 16 }}>{notice}</div>}

        <section className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>Reviewer prompts</h3>
          {PROMPT_FIELDS.map(f => {
            const isDefault = (data[f.key] || '') === (defaults[f.key] || '');
            return (
              <div className="form-group" key={f.key}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {f.label}
                  {isDefault
                    ? <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>default</span>
                    : <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--accent)' }}>customised</span>}
                  <button type="button" className="btn btn-ghost" style={{ fontSize: 12, marginLeft: 'auto' }}
                    disabled={isDefault} onClick={() => resetField(f.key)}>Reset to default</button>
                </label>
                <textarea className="form-control" rows={f.rows} style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12.5 }}
                  value={data[f.key] || ''} onChange={e => setField(f.key, e.target.value)} />
                <p className="form-hint">{f.hint}</p>
                {f.placeholders && placeholders.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                    {placeholders.map(ph => (
                      <code key={ph} style={{ background: 'var(--bg-elevated)', padding: '2px 6px', borderRadius: 4, fontSize: 11.5 }}>{ph}</code>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-primary" onClick={savePrompts} disabled={savingPrompts}>
              {savingPrompts ? 'Saving…' : 'Save prompts'}
            </button>
          </div>
        </section>

        <section className="card" style={{ padding: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>Standard rule packs</h3>
          <p className="form-hint" style={{ marginTop: 0 }}>
            Each enabled pack’s prompt is injected into the system prompt. Enable packs per config in the config editor.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {packs.map(pk => (
              <div key={pk.pack_key} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '14px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input className="form-control" style={{ maxWidth: 240, fontWeight: 600 }}
                    value={pk.name || ''} onChange={e => setPack(pk.pack_key, 'name', e.target.value)} />
                  <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>{pk.pack_key}</span>
                  <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={!!pk.enabled} onChange={e => setPack(pk.pack_key, 'enabled', e.target.checked)} /> Enabled
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input className="form-control" value={pk.description || ''} onChange={e => setPack(pk.pack_key, 'description', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Prompt (injected when enabled)</label>
                  <textarea className="form-control" rows={4} style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12.5 }}
                    value={pk.prompt_text || ''} onChange={e => setPack(pk.pack_key, 'prompt_text', e.target.value)} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" style={{ fontSize: 13 }} onClick={() => savePack(pk)}>Save pack</button>
                </div>
              </div>
            ))}
            {packs.length === 0 && <div className="form-hint">No rule packs found.</div>}
          </div>
        </section>
      </div>
    </div>
  );
}
